import { toLatLngLiteral } from "../../../utils/geo";
import type { CompanyLocation, SimpleLatLng } from "@mysnowscape/snow-lib";
import {
  LockPosition,
  ProposedRoute,
  RouteBuilderDraft,
  RouteBuilderProperty,
  RouteBuilderSession,
  RouteEndpointLocation,
  RouteStop,
  UnroutedJob,
} from "../../../types/routeBuilder";
import {
  normalizeRouteColor,
  ROUTE_COLOR_NAMES,
} from "../../../components/Maps/ActiveRoutes/routeColor";
import {
  createPropertyVisitId,
  createSessionId,
  incorporatesTime,
} from "../../../pages/Admin/Routes/RouteBuilder/lib/helpers";
import { defaultTemporaryExpiresAt } from "../../../pages/Admin/Routes/RouteBuilder/lib/defaultInputs";
import { initialPropertyServiceTime } from "../../../pages/Admin/Routes/RouteBuilder/lib/generationProperties";
import { checkedServiceKeys } from "../../../pages/Admin/Routes/RouteBuilder/lib/serviceHelpers";
import { persistedRouteType } from "../../../utils/routeType";
import type {
  RequestDrivingPathPayload,
  RequestGeneratePlanPayload,
  ResponseDrivingPathPayload,
  ResponseGeneratePlanPayload,
} from "@mysnowscape/snow-lib";
import type {
  ApplyPlanParams,
  ApplyPlanRequestPayload,
  DrivingPathJob,
  DrivingPathParams,
  EditableApiRoute,
  GeneratePlanParams,
  RouteBuilderApiRoute,
  RouteBuilderDraftDocument,
} from "./types";

/**
 * Pure serializers between Route Builder domain models and API/draft DTOs.
 * This module performs no HTTP requests.
 */

export function lockToApi(lock: LockPosition): true | undefined {
  if (lock === "start" || lock === "end") return true;
  return undefined;
}

/** Infer a UI lock from the API boolean and its position in the job list. */
export function lockFromApi(
  lockPosition: boolean | undefined,
  index: number,
  jobCount: number,
): LockPosition {
  if (lockPosition !== true || jobCount <= 0) return null;
  if (index === 0) return "start";
  if (index === jobCount - 1) return "end";
  return null;
}

/** Infer contiguous start/end lock blocks from the API's boolean markers. */
export function locksFromApi(
  lockPositions: Array<boolean | undefined>,
  existingLocks: LockPosition[] = [],
): LockPosition[] {
  const firstUnlocked = lockPositions.findIndex((locked) => locked !== true);
  if (firstUnlocked < 0) {
    return lockPositions.map((_, index) => existingLocks[index] ?? "start");
  }

  let lastUnlocked = lockPositions.length - 1;
  while (lastUnlocked >= 0 && lockPositions[lastUnlocked] === true) {
    lastUnlocked -= 1;
  }

  return lockPositions.map((locked, index) => {
    if (locked !== true) return null;
    if (index < firstUnlocked) return "start";
    if (index > lastUnlocked) return "end";
    return null;
  });
}

export function endpointToCompanyLocation(
  endpoint: RouteEndpointLocation | undefined,
  _companyId: string,
  fallbackName = "Location",
): CompanyLocation | null {
  const location = toLatLngLiteral(endpoint?.location);
  if (!endpoint || !location) {
    return null;
  }
  return {
    _id: endpoint._id || "temp-endpoint",
    name: endpoint.name || fallbackName,
    address: endpoint.address || "",
    location,
  };
}

function propertyById(
  properties: RouteBuilderProperty[],
  id: string,
): RouteBuilderProperty | undefined {
  return properties.find((p) => p.jobId === id);
}

function requiredEndpointLocation(
  endpoint: RouteEndpointLocation,
): SimpleLatLng {
  const location = toLatLngLiteral(endpoint.location);
  if (!location) {
    throw new Error("Route endpoint location is required");
  }
  return location;
}

export function toGeneratePlanRequest(
  params: GeneratePlanParams,
): RequestGeneratePlanPayload {
  const { inputs, properties, start, end, routeNamePrefix } = params;
  // Drafts saved before the flag existed have no value and count as on.
  const incorporateTime = incorporatesTime(inputs);
  return {
    currentCompanyId: params.companyId,
    incorporateTime,
    jobs: properties.map((property) => ({
      _id: property.jobId,
      name: property.name,
      location: toLatLngLiteral(property.location) ?? { lat: 0, lng: 0 },
      ...(incorporateTime && {
        estimatedServiceTimeSeconds: property.estimatedServiceTimeSeconds,
      }),
    })),
    routeCount: inputs.routeCount,
    // Without a max, the backend plans to its own default ceiling.
    ...(incorporateTime &&
      !params.withoutMaxRouteDuration && {
        maxRouteDurationSeconds:
          Math.max(1, inputs.maxRouteDurationMinutes) * 60,
      }),
    plannedDispatchTime: inputs.plannedDispatchTime,
    start: {
      location: toLatLngLiteral(start.location) ?? { lat: 0, lng: 0 },
    },
    end: {
      location: toLatLngLiteral(end.location) ?? { lat: 0, lng: 0 },
    },
    routeNamePrefix: routeNamePrefix.trim() || "Crew",
  };
}

function apiRouteToProposed(
  apiRoute: RouteBuilderApiRoute,
  index: number,
  properties: RouteBuilderProperty[],
  startDepot: RouteEndpointLocation,
  endDepot: RouteEndpointLocation,
  incorporateTime: boolean,
  existingId?: string,
  existingColor?: ProposedRoute["color"],
  existingStops: RouteStop[] = [],
): ProposedRoute {
  const unusedExistingStops = [...existingStops];
  const matchedExistingStops = apiRoute.jobs.map((job) => {
    const index = unusedExistingStops.findIndex(
      (stop) => stop.propertyId === String(job._id),
    );
    return index >= 0 ? unusedExistingStops.splice(index, 1)[0] : undefined;
  });
  const apiLocks = locksFromApi(
    apiRoute.jobs.map((job) => (job as DrivingPathJob).lockPosition),
    apiRoute.jobs.map((job, index) =>
      matchedExistingStops[index]
        ? matchedExistingStops[index]?.lock ?? null
        : propertyById(properties, String(job._id))?.lock ?? null,
    ),
  );
  const stops: RouteStop[] = apiRoute.jobs.map((job, order) => {
    const property = propertyById(properties, String(job._id));
    const existingStop = matchedExistingStops[order];
    const lockPosition = (job as DrivingPathJob).lockPosition;
    const lock =
      lockPosition === true ? apiLocks[order] : property?.lock ?? null;
    return {
      propertyVisitId: existingStop?.propertyVisitId ?? createPropertyVisitId(),
      propertyId: String(job._id),
      order,
      lock,
    };
  });

  const orderedProps = stops
    .map((s) => propertyById(properties, s.propertyId))
    .filter((p): p is RouteBuilderProperty => Boolean(p));

  const drivingDurationSeconds = apiRoute.drivingDurationSeconds ?? 0;
  // The API's total already includes driving time, in spite of the old field name. With time off
  // the backend sends no total. Then record no service time instead of adding up the properties'
  // default estimates.
  const apiTotalSeconds = incorporateTime
    ? apiRoute.totalDurationEstimateSeconds
    : undefined;
  const serviceDurationSeconds = !incorporateTime
    ? 0
    : apiTotalSeconds != null
      ? Math.max(0, apiTotalSeconds - drivingDurationSeconds)
      : orderedProps.reduce((t, p) => t + p.estimatedServiceTimeSeconds, 0);
  const totalDurationSeconds =
    apiTotalSeconds ?? drivingDurationSeconds + serviceDurationSeconds;

  const startCoords = toLatLngLiteral(apiRoute.start?.location);
  const endCoords = toLatLngLiteral(apiRoute.end?.location);
  const startLocation: RouteEndpointLocation = startCoords
    ? {
        ...startDepot,
        location: startCoords,
      }
    : startDepot;
  const endLocation: RouteEndpointLocation = endCoords
    ? {
        ...endDepot,
        location: endCoords,
      }
    : endDepot;

  // Prefer session color on merge (driving-path does not round-trip color).
  // Fall back to API, then palette by index (initial generate).
  const color = existingColor
    ? normalizeRouteColor(existingColor)
    : apiRoute.color
      ? normalizeRouteColor(apiRoute.color)
      : ROUTE_COLOR_NAMES[index % ROUTE_COLOR_NAMES.length];

  return {
    id: existingId ?? apiRoute._id ?? `route-builder-route-${index + 1}`,
    name: apiRoute.name,
    color,
    startLocation,
    endLocation,
    stops,
    instructions: apiRoute.details ?? "",
    metrics: {
      drivingDurationSeconds,
      serviceDurationSeconds,
      totalDurationSeconds,
      drivingDistanceMeters: apiRoute.drivingDistanceMeters ?? 0,
      drivingPath: apiRoute.drivingPath ?? "",
    },
  };
}

export function fromGeneratePlanResponse(
  response: ResponseGeneratePlanPayload,
  params: GeneratePlanParams,
): RouteBuilderSession {
  const { inputs, properties, start, end, companyId } = params;
  const routes = response.routes.map((route, index) =>
    apiRouteToProposed(
      route as RouteBuilderApiRoute,
      index,
      properties,
      start,
      end,
      incorporatesTime(inputs),
    ),
  );

  const unroutedJobs: UnroutedJob[] = (response.unassignedJobs ?? []).map(
    (job) => ({
      propertyVisitId: createPropertyVisitId(),
      jobId: String(job._id),
      name: job.name,
    }),
  );

  return {
    id: createSessionId("ui"),
    companyId,
    status: "generated",
    origin: "generated",
    inputs: { ...inputs },
    availableProperties: structuredClone(properties),
    routes,
    unroutedJobs,
    createdAt: inputs.plannedDispatchTime,
    expiresAt:
      inputs.routeType === "temporary"
        ? inputs.expiresAt ||
          defaultTemporaryExpiresAt(inputs.plannedDispatchTime)
        : undefined,
  };
}

function editableEndpoint(
  endpoint: EditableApiRoute["start"] | undefined,
): RouteEndpointLocation {
  const value = endpoint as
    | {
        _id?: string;
        id?: string;
        name?: string;
        address?: string;
        location?: SimpleLatLng;
      }
    | undefined;
  return {
    _id: value?._id ?? value?.id ?? null,
    name: value?.name ?? null,
    address: value?.address ?? null,
    location: toLatLngLiteral(value?.location),
  };
}

function endpointToApi(endpoint: RouteEndpointLocation) {
  const location = toLatLngLiteral(endpoint.location);
  return location
    ? {
        id: endpoint._id?.toString() ?? null,
        name: endpoint.name,
        address: endpoint.address,
        location,
      }
    : undefined;
}

/** Hydrate existing backend routes into one Route Builder edit session. */
export function fromEditableRoutes(
  apiRoutes: EditableApiRoute[],
  companyId: string,
): RouteBuilderSession {
  if (apiRoutes.length === 0) throw new Error("No routes selected");
  const season = apiRoutes[0].season;
  const routeType = persistedRouteType(apiRoutes[0]);
  if (
    apiRoutes.some(
      (route) =>
        route.season !== season || persistedRouteType(route) !== routeType,
    )
  ) {
    throw new Error("Selected routes must share one season and route type");
  }

  const properties = new Map<string, RouteBuilderProperty>();
  const routes: ProposedRoute[] = apiRoutes.map((route) => {
    const jobs = route.jobs ?? [];
    const stops: RouteStop[] = jobs.map((job, order) => {
      const jobId = String(job._id);
      const seasonDetails = season === "landscape" ? job.landscape : job.snow;
      const serviceKeys = checkedServiceKeys(seasonDetails);
      const serviceKey = serviceKeys[0];
      if (!properties.has(jobId)) {
        const notes = seasonDetails?.instructions ?? job.instructions ?? "";
        properties.set(jobId, {
          jobId,
          companyId: String(job.companyId ?? companyId),
          name: job.name,
          address: job.address ?? {},
          location: toLatLngLiteral(job.location) ?? { lat: 0, lng: 0 },
          propertyType: serviceKey ?? "residential",
          serviceType: serviceKey ?? "residential",
          ...(serviceKeys.length > 0 ? { serviceTypes: serviceKeys } : {}),
          zoneId: "unknown",
          zoneName: "Unknown",
          season,
          notes,
          originalNotes: notes,
          estimatedServiceTimeSeconds: initialPropertyServiceTime(job, season)
            .estimatedServiceTimeSeconds,
          serviceTimeSource: "manual",
          lock: null,
        });
      }
      return {
        propertyVisitId: createPropertyVisitId(),
        propertyId: jobId,
        order,
        lock: null,
      };
    });
    const serviceDurationSeconds = jobs.reduce(
      (total, job) =>
        total +
        initialPropertyServiceTime(job, season).estimatedServiceTimeSeconds,
      0,
    );
    return {
      id: route._id,
      name: route.name,
      color: normalizeRouteColor(route.color),
      startLocation: editableEndpoint(route.start),
      endLocation: editableEndpoint(route.end),
      stops,
      instructions: route.details ?? "",
      metrics: {
        drivingDurationSeconds: route.drivingDurationSeconds ?? 0,
        serviceDurationSeconds:
          route.totalDurationEstimateSeconds ?? serviceDurationSeconds,
        totalDurationSeconds:
          (route.drivingDurationSeconds ?? 0) +
          (route.totalDurationEstimateSeconds ?? serviceDurationSeconds),
        drivingDistanceMeters: route.drivingDistanceMeters ?? 0,
        drivingPath: route.drivingPath ?? "",
      },
    };
  });
  const defaultExpiration =
    routeType === "temporary" ? apiRoutes[0].expiresAt ?? undefined : undefined;
  const now = new Date().toISOString();

  return {
    id: createSessionId("edit"),
    companyId,
    status: "adjusted",
    origin: "edit",
    builderMode: "edit",
    inputs: {
      routeType,
      season,
      propertyFilters: { serviceTypes: [], recencyOfService: null },
      routeCount: routes.length,
      incorporateTime: true,
      maxRouteDurationMinutes: 480,
      plannedDispatchTime: now,
      expiresAt: defaultExpiration,
      startLocation: routes[0].startLocation,
      endLocation: routes[0].endLocation,
    },
    availableProperties: Array.from(properties.values()),
    routes,
    unroutedJobs: [],
    createdAt: now,
    expiresAt: defaultExpiration,
    routePersistence: Object.fromEntries(
      apiRoutes.map((route) => [
        route._id,
        {
          tags: Array.isArray(route.tags)
            ? route.tags.filter((tag): tag is string => typeof tag === "string")
            : [],
          isActive: route.isActive !== false,
          expiresAt: route.expiresAt,
        },
      ]),
    ),
  };
}

export function toDrivingPathRequest(
  params: DrivingPathParams,
): RequestDrivingPathPayload {
  const { session, routeIds, optimize } = params;
  const idSet = new Set(routeIds);
  const incorporateTime = incorporatesTime(session.inputs);
  return {
    currentCompanyId: session.companyId,
    optimize,
    incorporateTime,
    routes: session.routes
      .filter((route) => idSet.has(route.id))
      .map((route) => ({
        _id:
          session.routePersistence?.[route.id]?._id ??
          (route.id.startsWith("route-builder-") ? undefined : route.id),
        name: route.name,
        start: { location: requiredEndpointLocation(route.startLocation) },
        end: { location: requiredEndpointLocation(route.endLocation) },
        jobs: route.stops.map((stop) => {
          const property = propertyById(
            session.availableProperties,
            stop.propertyId,
          );
          const job: DrivingPathJob = {
            _id: stop.propertyId,
            name: property?.name ?? stop.propertyId,
            location: toLatLngLiteral(property?.location) ?? { lat: 0, lng: 0 },
            ...(incorporateTime && {
              estimatedServiceTimeSeconds:
                property?.estimatedServiceTimeSeconds ?? 3600,
            }),
            lockPosition: lockToApi(stop.lock ?? property?.lock ?? null),
          };
          return job;
        }),
      })),
  };
}

export function mergeDrivingPathResponse(
  session: RouteBuilderSession,
  response: ResponseDrivingPathPayload,
  requestedRouteIds: string[],
): RouteBuilderSession {
  const requested = new Set(requestedRouteIds);
  const requestedRoutes = session.routes.filter((route) =>
    requested.has(route.id),
  );
  const apiBySessionId = new Map(
    requestedRoutes.map((route, i) => [route.id, response.routes[i]]),
  );

  const routes = session.routes.map((route, index) => {
    if (!requested.has(route.id)) return route;
    const apiRoute =
      apiBySessionId.get(route.id) ??
      response.routes.find(
        (r) =>
          (r as RouteBuilderApiRoute)._id &&
          (r as RouteBuilderApiRoute)._id === route.id,
      );
    if (!apiRoute) return route;
    return apiRouteToProposed(
      { ...apiRoute, details: route.instructions } as RouteBuilderApiRoute,
      index,
      session.availableProperties,
      route.startLocation,
      route.endLocation,
      incorporatesTime(session.inputs),
      route.id,
      route.color,
      route.stops,
    );
  });

  return {
    ...session,
    status: "adjusted",
    routes,
  };
}

export function toApplyPlanRequest(
  params: ApplyPlanParams,
): ApplyPlanRequestPayload {
  const { session, companyId } = params;
  return {
    routes: session.routes.map((route) => {
      const persistence = session.routePersistence?.[route.id];
      const expiresAt = persistence
        ? persistence.expiresAt ?? undefined
        : session.expiresAt;
      const start = endpointToApi(route.startLocation);
      const end = endpointToApi(route.endLocation);
      return {
        ...(persistence ? { _id: persistence._id ?? route.id } : {}),
        name: route.name,
        details: route.instructions ?? "",
        color: route.color,
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
        companyId,
        season: session.inputs.season,
        jobIds: route.stops.map((stop) => stop.propertyId),
        drivingPath: route.metrics.drivingPath,
        drivingDistanceMeters: route.metrics.drivingDistanceMeters,
        drivingDurationSeconds: route.metrics.drivingDurationSeconds,
        ...(persistence
          ? { tags: persistence.tags, isActive: persistence.isActive }
          : {}),
        ...(session.inputs.routeType === "temporary" && expiresAt
          ? { expiresAt }
          : {}),
      };
    }),
  };
}

export function sessionToDraftData(
  session: RouteBuilderSession,
): RouteBuilderDraftDocument["data"] {
  return {
    origin: session.origin,
    // Preserve legacy draft snapshots without using them to build new plans.
    generationBaseline: structuredClone(session.generationBaseline),
    inputs: { ...session.inputs },
    routes: structuredClone(session.routes),
    availableProperties: structuredClone(session.availableProperties),
    unroutedJobs: structuredClone(session.unroutedJobs ?? []),
    builderMode: session.builderMode,
    routePersistence: session.routePersistence
      ? structuredClone(session.routePersistence)
      : undefined,
  };
}

export function draftDocumentToSession(
  doc: RouteBuilderDraftDocument,
): RouteBuilderSession {
  const data = doc.data;
  const routes = ((data.routes ?? []) as ProposedRoute[]).map((route) => ({
    ...route,
    color: normalizeRouteColor(route.color),
    stops: (route.stops ?? []).map((stop) => ({
      ...stop,
      propertyVisitId: stop.propertyVisitId || createPropertyVisitId(),
    })),
  }));
  const availableProperties = (data.availableProperties ??
    []) as RouteBuilderProperty[];
  const unroutedJobs = ((data.unroutedJobs ?? []) as UnroutedJob[]).map(
    (job) => ({
      ...job,
      propertyVisitId: job.propertyVisitId || createPropertyVisitId(),
    }),
  );

  return {
    id: `session-from-${doc._id}`,
    companyId: doc.companyId,
    status: "adjusted",
    origin: data.origin,
    generationBaseline: structuredClone(data.generationBaseline),
    inputs: { ...data.inputs },
    availableProperties: structuredClone(availableProperties),
    routes: structuredClone(routes),
    unroutedJobs: structuredClone(unroutedJobs),
    createdAt: doc.updatedAt ?? doc.createdAt ?? new Date().toISOString(),
    expiresAt:
      data.builderMode === "edit"
        ? data.inputs.expiresAt
        : data.inputs.routeType === "temporary"
          ? data.inputs.expiresAt ||
            defaultTemporaryExpiresAt(data.inputs.plannedDispatchTime)
          : undefined,
    builderMode: data.builderMode,
    routePersistence: data.routePersistence
      ? structuredClone(data.routePersistence)
      : undefined,
  };
}

export function draftDocumentToUiDraft(
  doc: RouteBuilderDraftDocument,
): RouteBuilderDraft {
  return {
    id: doc._id,
    name: doc.title || doc.data.name || "Untitled draft",
    savedAt: doc.updatedAt ?? doc.createdAt ?? new Date().toISOString(),
    origin: doc.data.origin,
    generationBaseline: structuredClone(doc.data.generationBaseline),
    inputs: doc.data.inputs,
    routes: (doc.data.routes as ProposedRoute[]).map((route) => ({
      ...route,
      color: normalizeRouteColor(route.color),
    })),
    availableProperties: doc.data.availableProperties as RouteBuilderProperty[],
    unroutedJobs: (doc.data.unroutedJobs ?? []) as UnroutedJob[],
    builderMode: doc.data.builderMode,
    routePersistence: doc.data.routePersistence,
  };
}

export function uiDraftToDocument(
  draft: RouteBuilderDraft,
  companyId: string,
): RouteBuilderDraftDocument {
  return {
    _id: draft.id,
    type: "route-builder",
    companyId,
    title: draft.name,
    data: {
      origin: draft.origin,
      generationBaseline: structuredClone(draft.generationBaseline),
      inputs: draft.inputs,
      routes: draft.routes,
      availableProperties: draft.availableProperties,
      unroutedJobs: draft.unroutedJobs ?? [],
      builderMode: draft.builderMode,
      routePersistence: draft.routePersistence,
    },
  };
}
